import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    Drawer,
    Grow,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
    Avatar,
} from '@mui/material';
import { SmartToy, Send, AccountCircle } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { keyframes } from '@mui/material';
import api from '../utils/api';

const typing = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
`;

const buildChatBacklog = (messages) =>
    messages
        .filter((message) => message.role !== 'system')
        .slice(-6)
        .map((entry) => `${entry.role === 'user' ? 'کاربر' : 'دستیار'}: ${entry.text}`)
        .join('\n');

const LINK_CATEGORY_LABELS = {
    service: 'خدمت رفاهی',
    news: 'خبر و اطلاع‌رسانی',
    event: 'رویداد',
    retraining: 'بازآموزی',
    board: 'اعضای هیئت مدیره',
};

const Assistant = () => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    // بازیابی پیام‌ها از localStorage اگر وجود داشت
    const [messages, setMessages] = useState(() => {
        const saved = localStorage.getItem('assistantMessages');
        return saved ? JSON.parse(saved) : [
            {
                role: 'assistant',
                text: `سلام، من دستیار هوشمند سازمان نظام پزشکی تبریز هستم.
پاسخ‌های من بر اساس اطلاعات موجود در اپلیکیشن ارائه می‌شود.

چطور می‌توانم به شما کمک کنم؟`,
            },
        ];
    });
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [, setContextSummary] = useState('');
    const [error, setError] = useState('');
    const [selectedLink, setSelectedLink] = useState(null);
    const messagesEndRef = useRef(null);

    const isInternalLink = (url) => {
        if (!url) return false;
        if (url.startsWith('/')) return true;
        if (typeof window === 'undefined') return false;
        try {
            const parsed = new URL(url, window.location.origin);
            return parsed.origin === window.location.origin;
        } catch {
            return false;
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };

    useEffect(() => {
        // اگر از صفحه‌ای با state: { fromAssistant: true } برگشتیم، اسکرول به پایین
        if (location.state?.fromAssistant) {
            scrollToBottom();
        }
    }, [location]);

    // ذخیره پیام‌ها در localStorage هر بار که تغییر کنند
    useEffect(() => {
        localStorage.setItem('assistantMessages', JSON.stringify(messages));
    }, [messages]);

    const handleSend = async () => {
        const trimmed = inputValue.trim();
        if (!trimmed || loading) return;
        setError('');
        const updated = [...messages, { role: 'user', text: trimmed }];
        setMessages(updated);
        setInputValue('');
        setLoading(true);

        try {
            const response = await api.post('/ai/query', {
                question: trimmed,
                previousChat: buildChatBacklog(updated),
            });
            const answerText =
                response?.data?.answer?.trim() ||
                'برای این زمینه اطلاعات کاملی ندارم؛ پایین همینجا لینک‌های مرتبط رو می‌بینی و احتمالاً مطالب پیشنهادی بیشتر کمک کنند.';
            const relatedLinks = Array.isArray(response?.data?.relatedLinks)
                ? response.data.relatedLinks.filter((link) => link?.url)
                : [];
            const searchGuidance = response?.data?.searchGuidance;
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    text: answerText,
                    links: relatedLinks,
                    guidance: searchGuidance,
                },
            ]);
            if (response?.data?.contextSummary) {
                setContextSummary(response.data.contextSummary);
            }
        } catch (err) {
            console.error('assistant error', err);
            if (err.response?.status === 403) {
                setError('محدودیت روزانه سوالات تمام شده است. لطفا فردا دوباره امتحان کنید.');
                setMessages((prev) => [
                    ...prev,
                    { role: 'assistant', text: 'محدودیت روزانه سوالات تمام شده است. لطفا فردا دوباره امتحان کنید.', links: [] },
                ]);
            } else {
                setError('ارتباط با سرور قطع شد. بعداً دوباره تلاش کنید.');
                setMessages((prev) => [
                    ...prev,
                    { role: 'assistant', text: 'متأسفانه پاسخی دریافت نشد؛ دوباره امتحان کنید.', links: [] },
                ]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOpenLinkDetails = (link) => setSelectedLink(link);
    const handleCloseLinkDetails = () => setSelectedLink(null);
    const handleOpenExternalLink = () => {
        if (!selectedLink?.url) return;
        if (isInternalLink(selectedLink.url)) {
            const target = new URL(selectedLink.url, window.location.origin);
            navigate(`${target.pathname}${target.search}${target.hash}`, {
                state: { fromAssistant: true },
            });
        } else if (typeof window !== 'undefined') {
            window.open(selectedLink.url, '_blank', 'noopener,noreferrer');
        }
        handleCloseLinkDetails();
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const accent = theme.palette.primary.main;
    const canSend = Boolean(inputValue.trim()) && !loading;

    return (
        <Box
            component="section"
            sx={{
                flex: 1,
                minHeight: 0,
                px: 0,
                pt: 0,
                pb: { xs: 1, md: 1 },
                boxSizing: 'border-box',
                color: theme.palette.text.primary,
                display: 'flex',
                flexDirection: 'column',
                mt: 5
            }}
        >
            <Box
                sx={{
                    maxWidth: 1100,
                    mx: 'auto',
                    flex: 1,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                }}
            >


                <Paper
                    elevation={1}
                    sx={{
                        backgroundColor: "transparent",
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'hidden' }}>
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.25,
                                p: 2,
                                maxHeight: "calc(100vh - 250px)",
                                overflow: "auto"
                            }}
                        >
                            {messages.map((message, index) => (
                                <Grow
                                    in
                                    timeout={400}
                                    key={`${message.role}-${index}`}
                                    style={{ transitionDelay: `${index * 40}ms` }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent={message.role === 'user' ? 'flex-start' : 'flex-end'}
                                        alignItems="flex-start"
                                        spacing={1}
                                    >
                                        {message.role === 'user' ? (
                                            <>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                                                    <AccountCircle sx={{ color: 'white' }} />
                                                </Avatar>
                                                <Box
                                                    sx={{
                                                        maxWidth: '85%',
                                                        px: 2,
                                                        py: 1.75,
                                                        borderRadius: 3,
                                                        border: `1px solid ${accent}`,
                                                        background: 'rgba(93,227,255,0.1)',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                    }}
                                                >
                                                    <Typography variant="body2" color="text.primary" sx={{ wordBreak: 'break-word' }}>
                                                        {message.text}
                                                    </Typography>
                                                    {message.links?.length ? (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                                            {message.links.map((link, linkIndex) => (
                                                                <Button
                                                                    key={`${link.url}-${linkIndex}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ textTransform: 'none' }}
                                                                    onClick={() => handleOpenLinkDetails(link)}
                                                                >
                                                                    {link.label || 'مشاهده'}
                                                                </Button>
                                                            ))}
                                                        </Stack>
                                                    ) : null}
                                                    {/* {message.guidance?.suggestions?.length ? (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                                            {message.guidance.suggestions.map((suggestion, idx) => (
                                                                <Chip
                                                                    key={`${suggestion}-${idx}`}
                                                                    label={suggestion}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    sx={{ textTransform: 'none' }}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    ) : null} */}
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <Box
                                                    sx={{
                                                        maxWidth: '85%',
                                                        px: 2,
                                                        py: 1.75,
                                                        borderRadius: 3,
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        background: theme.palette.action.hover,
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                    }}
                                                >
                                                    <Typography variant="body2" color="text.primary" sx={{ wordBreak: 'break-word' }}>
                                                        {message.text}
                                                    </Typography>
                                                    {message.links?.length ? (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                                            {message.links.map((link, linkIndex) => (
                                                                <Button
                                                                    key={`${link.url}-${linkIndex}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ textTransform: 'none' }}
                                                                    onClick={() => handleOpenLinkDetails(link)}
                                                                >
                                                                    {link.label || 'مشاهده'}
                                                                </Button>
                                                            ))}
                                                        </Stack>
                                                    ) : null}
                                                    {/* {message.guidance?.suggestions?.length ? (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                                            {message.guidance.suggestions.map((suggestion, idx) => (
                                                                <Chip
                                                                    key={`${suggestion}-${idx}`}
                                                                    label={suggestion}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    sx={{ textTransform: 'none' }}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    ) : null} */}
                                                </Box>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}>
                                                    <SmartToy sx={{ color: 'white' }} />
                                                </Avatar>
                                            </>
                                        )}
                                    </Stack>
                                </Grow>
                            ))}
                            {loading && (
                                <Grow in timeout={400}>
                                    <Stack direction="row" justifyContent="flex-end">
                                        <Box
                                            sx={{
                                                maxWidth: '85%',
                                                px: 2,
                                                py: 1.75,
                                                borderRadius: 2,
                                                border: `1px solid ${theme.palette.divider}`,
                                                background: theme.palette.action.hover,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <SmartToy sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                                            <Typography variant="body2" color="text.primary">
                                                صبر کن، دارم فکر می‌کنم...
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Box
                                                    sx={{
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        backgroundColor: theme.palette.primary.main,
                                                        animation: `${typing} 1.4s infinite ease-in-out`,
                                                        animationDelay: '0s',
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        backgroundColor: theme.palette.primary.main,
                                                        animation: `${typing} 1.4s infinite ease-in-out`,
                                                        animationDelay: '0.2s',
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        backgroundColor: theme.palette.primary.main,
                                                        animation: `${typing} 1.4s infinite ease-in-out`,
                                                        animationDelay: '0.4s',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Stack>
                                </Grow>
                            )}
                            <div ref={messagesEndRef} />
                        </Box>

                        <Divider />

                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}
                        <div style={{
                            display: 'flex', gap: 1,
                            paddingInline: 8,
                            alignItems: 'center'
                        }}>

                            <TextField
                                placeholder="سوالی دارید؟ بپرسید..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                multiline
                                minRows={1}
                                maxRows={4}
                                fullWidth
                                InputProps={{
                                    sx: {
                                        borderRadius: 2,
                                        background: theme.palette.action.selected,
                                        color: theme.palette.text.primary,
                                    },
                                    startAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                color="primary"
                                                onClick={handleSend}
                                                disabled={!canSend}
                                                sx={{
                                                    background: canSend ? accent : theme.palette.action.disabledBackground,
                                                    color: canSend ? theme.palette.common.white : theme.palette.action.disabled,
                                                    '&:hover': {
                                                        background: canSend ? theme.palette.primary.dark : undefined,
                                                    },
                                                }}
                                            >
                                                <Send />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                    </Box>
                </Paper>
            </Box>
            <Drawer
                anchor="bottom"
                open={Boolean(selectedLink)}
                onClose={handleCloseLinkDetails}
                ModalProps={{ keepMounted: true }}
                PaperProps={{
                    sx: {
                        borderRadius: '16px 16px 0 0',
                        px: { xs: 2, md: 3 },
                        py: 2,
                        backgroundColor: theme.palette.background.paper,
                    },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            {selectedLink?.category ? (
                                <Chip size="small" label={LINK_CATEGORY_LABELS[selectedLink.category] || 'پیوند مرتبط'} />
                            ) : null}
                            {selectedLink?.meta ? (
                                <Typography variant="caption" color="text.secondary">
                                    {selectedLink.meta}
                                </Typography>
                            ) : null}
                        </Stack>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {selectedLink?.label}
                        </Typography>
                        {selectedLink?.summary ? (
                            <Typography variant="body2" color="text.secondary">
                                {selectedLink.summary}
                            </Typography>
                        ) : null}
                        {/* لینک نمایش داده نشود */}
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleOpenExternalLink}
                            disabled={!selectedLink?.url}
                            sx={{ textTransform: 'none' }}
                        >
                            باز کردن صفحه
                        </Button>
                        <Button fullWidth variant="outlined" onClick={handleCloseLinkDetails} sx={{ textTransform: 'none' }}>
                            بستن
                        </Button>
                    </Stack>
                </Box>
            </Drawer>
        </Box>
    );
};

export default Assistant;
